import { useState } from "react";
import { toast } from "sonner";
import {
  Phone, Mail, Instagram, Linkedin, MapPin, Send, CheckCircle2, Loader2,
} from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { submitContactInquiry } from "@/lib/api";
import { CONTACT, SERVICES, DISCLAIMER } from "@/data/content";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  child_age: "",
  service: "",
  preferred_contact: "email",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email";
    if (!form.service) er.service = "Please choose a service";
    if (form.message.trim().length < 10) er.message = "Tell us a little more (at least 10 characters)";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitContactInquiry({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        organization: form.organization.trim() || null,
        child_age: form.child_age.trim() || null,
        service: form.service,
        preferred_contact: form.preferred_contact,
        message: form.message.trim(),
      });
      setSubmitted(true);
      toast.success("Message sent — check your inbox for a confirmation.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          ? String(err.response.data.detail)
          : "Something went wrong sending your message. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="container-site pb-24 pt-16 sm:pt-24" data-testid="contact-page">
      <p className="overline-label">Contact</p>
      <h1 className="mt-5 max-w-3xl font-heading text-4xl font-semibold leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
        <MaskedLine delay={0.1}>Tell us what's</MaskedLine>
        <MaskedLine delay={0.25}>
          <span className="text-terracotta-500">going on.</span>
        </MaskedLine>
      </h1>
      <Reveal delay={0.4}>
        <p className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
          Every message is read by a real person. We reply within one to two business days.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            {submitted ? (
              <div
                data-testid="contact-success-message"
                className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-3xl border border-border bg-card p-12 text-center shadow-soft"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-forest/10 text-forest dark:bg-terracotta-500/15 dark:text-terracotta-400">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h2 className="mt-6 font-heading text-2xl font-semibold text-foreground">
                  Thank you — we've got it.
                </h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
                  Your inquiry is on its way to our team, and a confirmation is heading to your
                  inbox. We'll be in touch within one to two business days.
                </p>
                <button
                  data-testid="contact-send-another-button"
                  onClick={() => { setSubmitted(false); setForm(INITIAL); }}
                  className="btn-secondary mt-8"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="rounded-3xl border border-border bg-card p-8 shadow-soft sm:p-10"
                noValidate
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="contact-name">Name *</Label>
                    <Input id="contact-name" data-testid="contact-name-input" value={form.name} onChange={set("name")} className="mt-2 h-12 rounded-xl bg-background" placeholder="Your full name" />
                    {errors.name && <p className="mt-1.5 text-xs text-destructive" data-testid="contact-name-error">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="contact-email">Email *</Label>
                    <Input id="contact-email" type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} className="mt-2 h-12 rounded-xl bg-background" placeholder="you@example.com" />
                    {errors.email && <p className="mt-1.5 text-xs text-destructive" data-testid="contact-email-error">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="contact-phone">Phone (optional)</Label>
                    <Input id="contact-phone" type="tel" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className="mt-2 h-12 rounded-xl bg-background" placeholder="(555) 000-0000" />
                  </div>
                  <div>
                    <Label htmlFor="contact-organization">Organization (optional)</Label>
                    <Input id="contact-organization" data-testid="contact-organization-input" value={form.organization} onChange={set("organization")} className="mt-2 h-12 rounded-xl bg-background" placeholder="School, clinic, or company" />
                  </div>
                  <div>
                    <Label htmlFor="contact-child-age">Child's Age (optional)</Label>
                    <Input id="contact-child-age" data-testid="contact-child-age-input" value={form.child_age} onChange={set("child_age")} className="mt-2 h-12 rounded-xl bg-background" placeholder="e.g. 7" />
                  </div>
                  <div>
                    <Label>Service Interested In *</Label>
                    <Select value={form.service} onValueChange={set("service")}>
                      <SelectTrigger data-testid="contact-service-select" className="mt-2 h-12 rounded-xl bg-background" aria-label="Service interested in">
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.filter((s) => !s.href).map((s) => (
                          <SelectItem key={s.slug} value={s.slug} data-testid={`contact-service-option-${s.slug}`}>
                            {s.title}
                          </SelectItem>
                        ))}
                        <SelectItem value="not-sure" data-testid="contact-service-option-not-sure">Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.service && <p className="mt-1.5 text-xs text-destructive" data-testid="contact-service-error">{errors.service}</p>}
                  </div>
                </div>

                <div className="mt-6">
                  <Label>Preferred Contact Method *</Label>
                  <RadioGroup
                    value={form.preferred_contact}
                    onValueChange={set("preferred_contact")}
                    className="mt-3 flex flex-wrap gap-3"
                    data-testid="contact-preferred-radio"
                  >
                    {[
                      { value: "email", label: "Email" },
                      { value: "phone", label: "Phone" },
                      { value: "text", label: "Text" },
                    ].map((o) => (
                      <label
                        key={o.value}
                        data-testid={`contact-preferred-${o.value}`}
                        className={`flex cursor-pointer items-center gap-2.5 rounded-full border px-5 py-2.5 text-sm transition-colors duration-300 ${
                          form.preferred_contact === o.value
                            ? "border-terracotta-500 bg-terracotta-500/5 text-foreground"
                            : "border-border text-muted-foreground hover:border-terracotta-500/50"
                        }`}
                      >
                        <RadioGroupItem value={o.value} aria-label={o.label} />
                        {o.label}
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                <div className="mt-6">
                  <Label htmlFor="contact-message">Message *</Label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={set("message")}
                    rows={5}
                    className="mt-2 rounded-xl bg-background"
                    placeholder="Tell us a little about what's happening and what you're hoping for…"
                  />
                  {errors.message && <p className="mt-1.5 text-xs text-destructive" data-testid="contact-message-error">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  className="btn-primary mt-8 w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="h-4 w-4" /> Send Message</>
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>

        <div className="space-y-6 lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-border bg-card p-8 shadow-soft" data-testid="contact-details-card">
              <h2 className="font-heading text-lg font-semibold text-foreground">Reach us directly</h2>
              <ul className="mt-6 space-y-5">
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-forest/10 text-forest dark:bg-sage/10 dark:text-sage">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Phone</p>
                    <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-phone-link" className="text-sm font-medium text-foreground transition-colors duration-300 hover:text-terracotta-500">{CONTACT.phone}</a>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-forest/10 text-forest dark:bg-sage/10 dark:text-sage">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Email</p>
                    <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="text-sm font-medium text-foreground transition-colors duration-300 hover:text-terracotta-500">{CONTACT.email}</a>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-forest/10 text-forest dark:bg-sage/10 dark:text-sage">
                    <Instagram className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Instagram</p>
                    <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="text-sm font-medium text-foreground transition-colors duration-300 hover:text-terracotta-500">@evolandtroppus</a>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-forest/10 text-forest dark:bg-sage/10 dark:text-sage">
                    <Linkedin className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">LinkedIn</p>
                    <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="text-sm font-medium text-foreground transition-colors duration-300 hover:text-terracotta-500">Evol &amp; Troppus</a>
                  </div>
                </li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div
              data-testid="contact-map-placeholder"
              className="flex h-64 flex-col items-center justify-center rounded-3xl border-2 border-dashed border-sage-400/60 bg-sage-100/40 p-8 text-center dark:border-forest-600 dark:bg-forest-900/30"
            >
              <MapPin className="h-7 w-7 text-forest dark:text-sage" />
              <p className="mt-4 text-sm font-medium text-foreground">Serving families everywhere</p>
              <p className="mt-2 max-w-xs text-xs leading-relaxed text-muted-foreground">
                Virtual sessions available worldwide. Google Maps embed coming soon for in-person visits.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.26}>
            <div data-testid="contact-disclaimer" className="rounded-3xl border border-border bg-muted/40 p-6">
              <p className="text-xs leading-relaxed text-muted-foreground">{DISCLAIMER}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
